import { Comic, ElType } from "../classes/comic.class";
import { encolherParaCaber } from "./encolher-imagem";
import Firebase from "./firebase.class";
import Utils from "../classes/utils.class";
import { ehCenarioPronto, urlDoCenarioPronto } from "../classes/cenarios-prontos";

export type CreateAppUserObj = {
  name: string;
  email: string;
  city: string;
  educationalInstitution: string;
  birthDate: string;
  /**
   * ESTUDANTE OU EDUCADOR, declarado no cadastro (T2.68 do pedido T2).
   *
   * "na parte de criar conta vai ter que ter uma questao de tipo estudante ou professor e tal" —
   * e o T2.72 fecha pela obrigatoriedade: "devia colocar como obrigatorio isso, no minimo se
   * tiver estudante ou e [professor] para poder abrir esse tipo de opcao".
   *
   * OPCIONAL NO TIPO, E ISSO NAO E CONTRADICAO: obrigatorio no FORMULARIO, ausente nas contas
   * que existiam antes deste campo. Marcar como obrigatorio aqui faria o TypeScript exigir o
   * campo de toda conta antiga, e o unico jeito de satisfazer isso seria inventar um valor para
   * cada uma — o que e pior do que nao saber. Quem nao declarou nao pode pedir instituicao
   * (`podeSolicitarInstituicao(undefined) === false`), e a tela pede.
   *
   * E ele NAO AUTORIZA NADA. O cliente escreve este campo, e quem escreve pode mentir: bastaria
   * trocar "estudante" por "educador" para virar moderador. Quem autoriza e o VINCULO, conferido
   * em `firestore.rules`.
   */
  accountType?: "estudante" | "educador";
};
/**
 * T24 · O QUE ENTROU NO DESENHO, guardado junto com ele. O operador: «quando eu salvar um personagem,
 * eu tenho que salvar os metadados (...) do prompt que entrou naquele personagem (...) se eu uso a
 * galeria e eu vou criar aquele outro personagem em uma outra pose, eu uso não só a imagem de
 * referência, como eu uso o prompt».
 */
export type MetadadosDaImagem = {
  descricao?: string;
  descritivo?: string;
  etiquetas?: string[];
  estilo?: string;
};
export type ImgInfo = {
  type: ElType;
  url: string;
  createdAt?: number;
} & MetadadosDaImagem;
type GeneratedImgStatus = "generating" | "blocked" | "ok";
export type GeneratedImgInfo = {
  prompt: string;
  createdAt: number;
  ids: string[];
  status: GeneratedImgStatus;
};
export type AppUserObj = CreateAppUserObj & {
  id: string;
  _U: string;
  comics: Comic[];
  imgs: Record<string, ImgInfo>;
  generatedImages: Record<string, GeneratedImgInfo>;
};

export default class AppUser {
  private id: string;
  private name: string;
  private email: string;
  private city: string;
  private educationalInstitution: string;
  private birthDate: string;
  private accountType?: "estudante" | "educador";
  private _U: string;
  private comics: Comic[];
  private imgs: Record<string, ImgInfo>;
  private generatedImages: Record<string, GeneratedImgInfo>;

  constructor(appUserObj: AppUserObj) {
    this.id = appUserObj.id;
    this.name = appUserObj.name;
    this.email = appUserObj.email;
    this.city = appUserObj.city;
    this.educationalInstitution = appUserObj.educationalInstitution;
    this.birthDate = appUserObj.birthDate;
    this.accountType = appUserObj.accountType;
    this._U = appUserObj._U;
    this.comics = appUserObj.comics;
    this.imgs = appUserObj.imgs;
    this.generatedImages = appUserObj.generatedImages;
  }

  async update(data: any) {
    const _data: any = { ...data };
    delete _data.email;
    await Firebase.updateDocument("users", this.id, _data);
  }

  toObj(): AppUserObj {
    return {
      id: this.id,
      name: this.name,
      email: this.email,
      city: this.city,
      educationalInstitution: this.educationalInstitution,
      birthDate: this.birthDate,
      accountType: this.accountType,
      _U: this._U,
      comics: this.comics,
      imgs: this.imgs,
      generatedImages: this.generatedImages,
    };
  }

  copy() {
    return new AppUser(this.toObj());
  }

  getImgUrl(id: string) {
    // T11.6 · os seis cenarios prontos moram no produto, nao na conta (ver cenarios-prontos.ts).
    if (ehCenarioPronto(id)) return urlDoCenarioPronto(id);
    return this.imgs[id]?.url || "";
  }

  async saveImg(src: string, type: ElType, metadados?: MetadadosDaImagem) {
    let url = src;
    try {
      const blob = await (await fetch(src)).blob();
      url = await Utils.blobToBase64(blob);
    } catch (e) {
      // CORS or network error — save the URL directly
      console.warn("Could not convert to base64, saving URL:", e);
    }

    // ── O DESENHO PRECISA CABER NO DOCUMENTO (T5.6) ──────────────────────────────────────
    //
    // Um documento do Firestore cabe em 1 MiB, e aqui a imagem vai INTEIRA no campo `url`, em
    // base64. Medido em 15/09 contra produção: um desenho detalhado chega a 1,60 MiB — e a
    // gravação falha inteira, sem nada explicar.
    //
    // É o mesmo teto que derrubava o salvar do roteirizador (T5.6). Este caminho é o outro: o
    // botão "Gerar imagem" do editor, que põe o desenho na prateleira de Personagens, Cenários
    // ou Figuras. Os dois estavam quebrados pela mesma razão, e o conserto é o mesmo.
    const encolhida = await encolherParaCaber(url);
    if (encolhida.tentativaUsada) {
      console.info(
        `[saveImg] desenho reencodado para caber: ${Math.round(encolhida.bytesAntes / 1024)}KB -> ` +
          `${Math.round(encolhida.bytesDepois / 1024)}KB`
      );
    }
    url = encolhida.imagem;

    // Só os campos que vieram: o Firestore recusa `undefined` no documento.
    const meta = Object.fromEntries(
      Object.entries(metadados ?? {}).filter(([, v]) => v !== undefined && v !== "" && !(Array.isArray(v) && !v.length))
    );
    await Firebase.pushDocument(`users/${this.id}/imgs`, {
      type,
      url,
      createdAt: Date.now(),
      ...meta,
    } as ImgInfo);
  }

  Id(value?: string) {
    if (value !== undefined) {
      this.id = value;
    }

    return this.id;
  }
  Name(value?: string) {
    if (value !== undefined) {
      this.name = value;
    }
    return this.name;
  }
  Email(value?: string) {
    if (value !== undefined) {
      this.email = value;
    }
    return this.email;
  }
  City(value?: string) {
    if (value !== undefined) {
      this.city = value;
    }
    return this.city;
  }
  EducationalInstitution(value?: string) {
    if (value !== undefined) {
      this.educationalInstitution = value;
    }
    return this.educationalInstitution;
  }
  BirthDate(value?: string) {
    if (value !== undefined) {
      this.birthDate = value;
    }
    return this.birthDate;
  }
  AccountType(value?: "estudante" | "educador") {
    if (value !== undefined) {
      this.accountType = value;
    }
    return this.accountType;
  }
  __U(value?: string) {
    if (value !== undefined) {
      this._U = value;
    }
    return this._U;
  }
  Comics(value?: Comic[]) {
    if (value !== undefined) {
      this.comics = value;
    }
    return this.comics;
  }
  Imgs(value?: Record<string, ImgInfo>) {
    if (value !== undefined) {
      this.imgs = value;
    }
    return this.imgs;
  }
  GeneratedImages(value?: Record<string, GeneratedImgInfo>) {
    if (value !== undefined) {
      this.generatedImages = value;
    }
    return this.generatedImages;
  }
}
